import React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';

import { FormStore } from 'libs/forms/app';
import { FieldsWrapper } from 'libs/forms/styles';
import { confToFields } from 'libs/forms';

import { Stack } from '@chakra-ui/react';

import { SpacesQuery } from 'apps/space/gql';
import { SpaceModel, SpacesApp } from 'apps/space/app';

import {
  FilterField,
  FilterOps,
  FilterInput,
  ValueInputType,
} from '../filterModels';

const SpaceFilterView = observer(({ filter }) => {
  const { formStore } = filter;
  const refs = {};
  return (
    <Stack py={3} px={2} backgroundColor='#fbfbfb'>
      <FieldsWrapper formColumns={2}>
        {confToFields({
          formStore,
          refs,
          responsive: true,
        })}
      </FieldsWrapper>
      ;
    </Stack>
  );
});

const conf = {
  fields: {
    formSpaces: {
      label: 'Add Customer Spaces',
      type: 'spacesComplete',
      shouldRenderFieldLabel: false,
    },
  },
  spacesForm: {
    fields: ['formSpaces'],
  },
};

export class SpaceFilter {
  /* Filters represents UI state of the filter.
  Filters are instantiated by the paginationOffsetManager or equivalent.
  These may be re-instantiated in which case the caller should call filter.dispose?.() */
  static View = SpaceFilterView;
  static OpJoin = 'OR';
  @observable formSpaces = [];

  @observable queries = new Map();

  constructor({ fieldName, fieldTitle, selected, filterInputs }) {
    this.formStore = new FormStore(this, conf, 'spacesForm', '', false);
    this.formSpaces = new SpacesApp();

    this.filterField = new FilterField({
      filter: this,
      filterInputs,
      fieldName,
      fieldTitle,
      selected,
    });

    const spaceIds = this.filterField.filterInputs.map(
      filInput => filInput.value.value,
    );

    const q = new SpacesQuery(this, { variables: { ids: spaceIds } });
    q.process(
      spaces => {
        if (spaces) {
          return spaces.map(space => SpaceModel.Load(space));
        }
        return [];
      },
      spaces => {
        this.UpdateSpaces(spaces);
      },
    );
    q.query();

    // When the fieldStore target changes (formSpaces), write the filterInputs used for generating URLs and API calls
    // Not a 'reaction' just a callback
    this.formSpaces.onChange(this.saveFilterInputs);
  }

  @action UpdateSpaces = spaces => {
    /* When we load a 'Connection' e.g. paginationOffset.js we initiate filters.  */
    this.ClearSpaces();
    spaces.forEach(space => this.formSpaces.Add(space));
  };

  @computed get getFilterInputs() {
    const filterInputs = [];
    this.formSpaces
      .filter(
        space =>
          filterInputs.findIndex(flInput => flInput.value === space.id) === -1,
      )
      .forEach(space =>
        filterInputs.push(
          new FilterInput({
            field: this.filterField.name,
            value: space.id,
            valueObject: space,
            type: ValueInputType.STRING,
            operator: FilterOps.EQ,
          }),
        ),
      );
    return filterInputs;
  }

  @action saveFilterInputs = () => {
    const { filterInputs } = this.filterField;

    const { formSpaces } = this;
    filterInputs.clear();
    formSpaces.forEach(space => {
      const ix = filterInputs.findIndex(flInput => flInput.value === space.id);
      if (ix === -1) {
        filterInputs.push(
          new FilterInput({
            field: this.filterField.name,
            value: space.id,
            valueObject: space,
            type: ValueInputType.STRING,
            operator: FilterOps.EQ,
          }),
        );
      }
    });
  };

  @action AddSpace = space => {
    this.formSpaces.Add(space);
  };

  @action ClearSpaces = () => {
    this.formSpaces.clear();
  };

  @action OnApplyingFilter = () => {
    /* Clear spaces from filter when filters are applied and the filter is not selected */
    if (!this.filterField.selected) {
      this.ClearSpaces();
    }
  };

  @computed get isValid() {
    return true;
  }

  dispose() {
    // eslint-disable-next-line no-restricted-syntax
    for (const query of this.queries.values()) {
      if (query.disposer) {
        query.disposer();
      }
    }
  }
}
